import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const PREFIX = 'ds-';

const isChemelexToken = (value: string) => value.startsWith('chemelex-');

/*
 * tailwind-merge's own `prefix` option expects Tailwind v4's `ds:` syntax; ours
 * is v3's `ds-`, after any variant (`hover:ds-bg-accent`, `-ds-ml-3`). Strip it
 * so the class is recognised, and treat unprefixed classes (MUI, base.scss
 * hooks) as foreign: never merged or dropped.
 */
const twMerge = extendTailwindMerge({
  experimentalParseClassName: ({ className, parseClassName }) => {
    const parsed = parseClassName(className);
    const base = parsed.baseClassName;
    const negative = base.startsWith(`-${PREFIX}`);
    if (!negative && !base.startsWith(PREFIX)) return { ...parsed, isExternal: true };

    return {
      ...parsed,
      baseClassName: negative ? `-${base.slice(PREFIX.length + 1)}` : base.slice(PREFIX.length),
      maybePostfixModifierPosition: parsed.maybePostfixModifierPosition === undefined
        ? undefined
        : parsed.maybePostfixModifierPosition - PREFIX.length,
    };
  },
  /* Our own theme keys are `chemelex-*` (tailwind.config.js); without this an
   * unknown `text-chemelex-h1` is read as a colour, not a size. */
  extend: {
    theme: {
      text: [isChemelexToken],
      shadow: [isChemelexToken],
      radius: [isChemelexToken],
      spacing: [isChemelexToken],
    },
  },
});

/**
 * shadcn/ui's class combiner: tailwind-merge drops overridden utilities, so a
 * caller's className wins. Legacy components keep using `classnames`.
 */
export function cn (...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
